<template>
  <Card cardClasses="shadow-sm border-0">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary"><i class="bi bi-file-earmark-text me-2"></i>Quotations Management</h4>
      <div>
        <button v-if="profile.role !== 'user'" class="btn btn-primary" @click="openQuotationModal"><i class="bi bi-plus-circle me-1"></i> New Quotation</button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading quotations...</p>
      </div>

      <div v-if="!loading" class="quotations-content fade-in">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ quotations.total || 0 }}</span>
            <span class="text-muted">Quotations</span>
          </div>
          <UInput v-model="dataTableSearch" size="md" placeholder="Search quotation" class="mb-0 w-auto" />
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div class="table-responsive">
            <table class="table table-hover align-middle mb-0">
              <thead>
              <tr>
                <th>#</th>
                <th>CODE</th>
                <th>CUSTOMER</th>
                <th>STORE</th>
                <th>CREATED BY</th>
                <th>DISCOUNT</th>
                <th>CREATED</th>
                <th class="text-end">ACTIONS</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(row, index) in paginatedQuotations" :key="row.id" class="quotation-row" :class="{'bg-light': index % 2 === 0}">
                <td>{{ index + 1 + (dataTablePage-1)*dataTablePerPage }}</td>
                <td>{{ row.code || row.id }}</td>
                <td>{{ row.customer.name }}</td>
                <td>{{ row.store.name }}</td>
                <td>{{ row.user.first_name + ' ' + row.user.last_name }}</td>
                <td>{{ formatNumber(row.discount) }}</td>
                <td>{{ formatDate(row.created_at) }}</td>
                <td class="text-end">
                  <button class="btn btn-sm btn-outline-primary" @click="quotationDetails(row.id)"><i class="bi bi-eye"></i> Details</button>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{ item }}</option>
            </select>
          </div>
          <div>
            <Pagination v-model="dataTablePage" :pages="totalPages" :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>
      </div>
    </div>
  </Card>

  <!-- Add Quotation Modal -->
  <Modal :open="quotationModal" @closed="quotationModal=false" dialog-size="modal-lg">
    <div class="modal-header bg-light">
      <h5 class="modal-title m-0"><i class="bi bi-plus-circle me-2"></i>Add Quotation</h5>
      <button type="button" class="btn-close" @click="quotationModal=false"></button>
    </div>
    <div class="modal-body">
      <form @submit.prevent="submitQuotation">
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">Customer</label>
            <select class="form-select" v-model="quotationForm.customer_id" data="">
              <option disabled value="">Select customer</option>
              <option v-for="c in customers" :key="c.id" :value="c.id">{{c.name}}</option>
            </select>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">Store</label>
            <select class="form-select" v-model="quotationForm.store_id" data="">
              <option disabled value="">Select store</option>
              <option v-for="s in stores" :key="s.id" :value="s.id">{{s.name}}</option>
            </select>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label fw-bold">Discount</label>
            <div class="input-group"><span class="input-group-text">%</span>
              <input type="number" min="0" class="form-control" v-model.number="quotationForm.discount">
            </div>
          </div>
        </div>
        <div class="mb-3">
          <label class="form-label fw-bold">Notes</label>
          <textarea class="form-control" rows="3" v-model="quotationForm.notes" placeholder="Optional notes"></textarea>
        </div>
      </form>
    </div>
    <div class="modal-footer bg-light">
      <button class="btn btn-outline-secondary" @click="quotationModal=false"><i class="bi bi-x-circle me-1"></i>Cancel</button>
      <button class="btn btn-primary" @click="submitQuotation"><i class="bi bi-plus-circle me-1"></i>Create Quotation</button>
    </div>
  </Modal>
  <!-- End Modal -->
</template>

<script>
import quotationService from '@/api/quotationService'
import customerService from '@/api/customerService'
import storeService from '@/api/storeService'
import router from '@/router'
import { decrypt } from '@/utils/crypto'
import Pagination from '@/components/pagination/Pagination.vue'
import Modal from '@/components/modal/Modal'
import formatNumber from '@/utils/formatNumber'
import formatDate from "../../utils/formatDate";

export default {
  name: 'Quotations',
  components: { Pagination, Modal },
  data(){
    return {
      profile:{},
      loading:true,
      quotations: {
        data: []
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
      quotationModal:false,
      quotationForm:{
        customer_id:'',
        store_id:'',
        discount:0,
        notes:''},
      customers:[],
      stores:[],
      organization:{},
    }
  },
  methods:{
    formatDate,
    formatNumber,
    async getQuotations(){
      this.loading = true
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
      }
      try{ this.quotations = await quotationService.getQuotations(this.quotationForm.store_id, params); }
      catch(e){ console.error(e);} finally { this.loading=false; }
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getQuotations()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getQuotations()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 10 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
    quotationDetails(id){ router.push('/quotation/'+id) },
    openQuotationModal(){
      this.quotationForm={customer_id:'',store_id:'',discount:0,notes:''};
      this.quotationModal=true;
    },
    async submitQuotation(){
      try{
        const payload = { ...this.quotationForm };
        await quotationService.createQuotation(payload);
        this.quotationModal=false;
        await this.getQuotations();
      }catch(e){ console.error(e);}
    },
    async loadDropdowns(){
      try{
        const customersData = await customerService.getCustomers({ per_page: 1000 });
        this.customers = Array.isArray(customersData?.data) ? customersData.data.filter(c => c != null) : [];
        const storesData = await storeService.getStores();
        this.stores = Array.isArray(storesData?.data) ? storesData.data.filter(s => s != null) : (Array.isArray(storesData) ? storesData.filter(s => s != null) : []);
      }catch(e){console.error(e);}
    },
  },
  computed:{
    totalPages(){ return this.quotations?.last_page || 1; }
  },
  watch:{
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getQuotations()
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getQuotations()
    },
  },
  async created(){ this.profile=JSON.parse(decrypt(sessionStorage.getItem('profile'))); this.organization=JSON.parse(decrypt(sessionStorage.getItem('organization'))); await Promise.all([this.getQuotations(),this.loadDropdowns()]); }
}
</script>

<style scoped>
.quotations-content{ padding:0.5rem; }
.table th{ font-weight:600; text-transform:uppercase; font-size:0.7rem; letter-spacing:0.5px; color:#6b7280; border-top:none; padding:1rem 1.5rem; background:#f9fafb; border-bottom:1px solid #e9ecef; }
.table td{ padding:1rem 1.5rem; vertical-align:middle; border-color:#e9ecef; color:#4b5563; }
.quotation-row{ transition:all 0.2s ease; }
.quotation-row:hover{ background:#f8f9fa; transform:translateY(-2px); box-shadow:0 4px 6px -1px rgba(0,0,0,0.05); }
</style>
